import React, { useContext, useEffect, useMemo } from 'react';
import { useAuth } from '@components/amplifyProvider';
import { ErrorTypes } from '@fabric-ui/components';
import { QueryKey } from '@fabric-ui/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAccountTokenFromLS } from '@utils/auth.utils';
import { useAccountId } from '@utils/router.utils';
import { decodeJWT } from 'aws-amplify/auth';
import { AuthPlatform, UserWithAdditionalInfo } from '../auth/auth.types';
import { getAdditionalUserData } from '../auth/cognitoAuthentication';
import useHandleError from '../hooks/useHandleError';
import { defaultUser, routerContextDefaultValues, useRouterContext } from '../RouterContext';
import useApi from '../utils/useApi';
import { sharedWorkerService } from '../workers/sharedWorkerService';
import { AuthContextType } from './types';

export const AuthContext = React.createContext<AuthContextType>({
  ...routerContextDefaultValues,
  accessToken: '',
  accountId: '',
  api: undefined,
  authPlatform: AuthPlatform.COGNITO,
  refreshAccessToken: () => new Promise(() => {}),
  refreshAccessTokenAndAccountData: () => new Promise(() => {}),
  refetchAdditionalData: () => undefined,
  user: {
    accountId: '',
    accounts: [{ accountId: '', accountName: '', userRoleId: '' }],
    permissions: '',
    roleId: '',
    userId: '',
    favoriteApps: [],
    email: '',
    userName: '',
  },
});

const useUserAdditionalInfo = ({ authUser, api, enableRequests, authPlatform }) =>
  useQuery<UserWithAdditionalInfo>({
    queryKey: [QueryKey.UserAdditionalInfo, JSON.stringify(authUser)],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      api(authPlatform ? authPlatform.getAdditionalUserData : getAdditionalUserData, {
        signal,
        onSuccess: ({ data }) => ({
          ...authUser,
          ...(authPlatform.provider === AuthPlatform.ZID ? data?.getCustomerFilteredUserAdditionalData : data?.getUserAdditionalData),
        }),
      }),
    enabled: enableRequests && !!authUser.userId,
  });

const useFeatureFlags = ({ api, enableRequests, onSuccess = d => d }) =>
  useQuery({
    queryKey: [QueryKey.FeatureFlags],
    queryFn: ({ signal }: { signal?: AbortSignal }) => api('feature-flags', { isJson: false, signal, onError: () => false, onSuccess }),
    enabled: enableRequests,
    retry: 10,
    retryDelay: 1000,
  });

export const defaultUserWithAdditionalInfo: UserWithAdditionalInfo = { ...defaultUser, accounts: [], favoriteApps: [] };

export const AuthContextProvider = ({ children }: React.PropsWithChildren) => {
  const handleError = useHandleError();
  const { refreshAccessToken, authPlatform } = useAuth();
  const {
    user: authUser,
    isAuthenticated,
    isLoading: loadingAuth,
    userIsActive,
    featureFlags,
    userPermissions,
    setFeatureFlags,
  } = useRouterContext();
  const accountId = useAccountId();
  const client = useQueryClient();
  const accessToken = getAccountTokenFromLS(accountId);
  const api = useApi(accountId, refreshAccessToken, featureFlags);

  const getAccountFromToken = decodedToken =>
    authPlatform ? authPlatform.getAccountFromToken(decodedToken) : decodeJWT(accessToken).payload!.account_id;

  const enableRequests = !!accountId && !!accessToken && accountId === getAccountFromToken(decodeJWT(accessToken));

  const {
    data: user = defaultUserWithAdditionalInfo,
    isPending: isLoadingUser,
    refetch: refetchAdditionalData,
    error: userError,
  } = useUserAdditionalInfo({ authUser, api, enableRequests, authPlatform });

  const { isPending: isLoadingFeatureFlags, error: ffError } = useFeatureFlags({ api, enableRequests, onSuccess: setFeatureFlags });

  useEffect(() => {
    if (accountId) {
      if (userIsActive) {
        sharedWorkerService.notifyWorkerOnActiveTabEvent(accountId);
        refreshAccessToken(accountId);
      } else {
        sharedWorkerService.notifyWorkerOnInactiveTabEvent(accountId);
      }
    }
  }, [userIsActive, accountId /* , refreshAccessToken */]);

  const error = userError || ffError;
  if (error) {
    handleError(error);
    console.error(ErrorTypes.AvContext, error);
    throw new Error(ErrorTypes.AvContext);
  }

  const refreshAccessTokenAndAccountData = async () => {
    await refreshAccessToken(accountId, true);
    client.clear();
  };

  const value = useMemo(
    () => ({
      accountId,
      accessToken,
      featureFlags,
      isLoading: isLoadingUser || isLoadingFeatureFlags || !Object.keys(featureFlags).length || loadingAuth,
      isAuthenticated: enableRequests && isAuthenticated,
      user,
      api,
      userPermissions,
      userIsActive,
      authPlatform: authPlatform?.provider,
      refreshAccessTokenAndAccountData,
      refetchAdditionalData,
      refreshAccessToken,
    }),
    [
      accountId,
      accessToken,
      featureFlags,
      loadingAuth,
      isLoadingUser,
      isLoadingFeatureFlags,
      isAuthenticated,
      user,
      api,
      userPermissions,
      authPlatform,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => useContext(AuthContext);
